/**
 * 移动端视口高度修复工具
 * 解决移动端浏览器地址栏和工具栏导致的视口高度变化问题
 */

/**
 * 设置CSS自定义属性来修复视口高度
 */
export const setViewportHeight = (): void => {
  // 获取实际视口高度
  const vh = window.innerHeight * 0.01;
  const dvh = window.visualViewport?.height ? window.visualViewport.height * 0.01 : vh;
  
  // 设置CSS自定义属性
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--dvh', `${dvh}px`);
};

/**
 * 修复移动端布局偏移问题
 */
export const fixMobileLayoutOffset = (): void => {
  if (!isMobileDevice()) return;
  
  // 防止页面水平滚动
  document.body.style.overflowX = 'hidden';
  document.body.style.position = 'relative';
  document.body.style.width = '100%';
  document.body.style.maxWidth = '100vw';
  
  // 修复根容器
  const root = document.getElementById('root');
  if (root) {
    root.style.overflowX = 'hidden';
    root.style.width = '100%';
    root.style.maxWidth = '100vw';
    root.style.position = 'relative';
  }
  
  // 修复聊天容器
  const chatContainer = document.querySelector('.chat-container');
  if (chatContainer) {
    (chatContainer as HTMLElement).style.overflowX = 'hidden';
    (chatContainer as HTMLElement).style.width = '100%';
    (chatContainer as HTMLElement).style.maxWidth = '100vw';
    (chatContainer as HTMLElement).style.position = 'relative';
  }
};

/**
 * 防止移动端页面自动缩放
 */
export const preventMobileZoom = (): void => {
  if (!isMobileDevice()) return;
  
  // 设置viewport meta标签
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    document.head.appendChild(viewport);
  }
  
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
  
  // 防止文本自动调整大小
  document.documentElement.style.setProperty('-webkit-text-size-adjust', '100%');
  document.documentElement.style.setProperty('-ms-text-size-adjust', '100%');
  document.documentElement.style.setProperty('text-size-adjust', '100%');
  
  // 设置触摸操作
  document.documentElement.style.setProperty('touch-action', 'manipulation');
  
  // 防止用户选择文本导致缩放
  document.documentElement.style.setProperty('-webkit-user-select', 'none');
  document.documentElement.style.setProperty('-moz-user-select', 'none');
  document.documentElement.style.setProperty('-ms-user-select', 'none');
  document.documentElement.style.setProperty('user-select', 'none');
};

/**
 * 修复长文本导致的页面变宽问题
 */
export const fixLongTextOverflow = (): void => {
  if (!isMobileDevice()) return;
  
  // 为所有可能包含长文本的元素添加样式
  const style = document.createElement('style');
  style.textContent = `
    @media (max-width: 768px) {
      /* 移除全局的max-width: 100%设置，避免覆盖建议框宽度 */
      /* 只对特定元素应用宽度限制 */
      pre, code {
        max-width: 100% !important;
        white-space: pre-wrap !important;
        overflow-x: auto !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
      }
      
      table {
        max-width: 100% !important;
        table-layout: fixed !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
      }
      
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      
      /* 确保建议框不受全局规则影响 */
      .clarification-hint,
      .force-analysis-option,
      .message-actions,
      .file-attachment {
        max-width: 90% !important;
        width: 90% !important;
        box-sizing: border-box !important;
      }
      
      /* 覆盖可能的父级设置 */
      .chat-message-content .clarification-hint,
      .chat-message-content .force-analysis-option,
      .chat-message-content .message-actions,
      .chat-message-content .file-attachment {
        max-width: 90% !important;
        width: 90% !important;
        box-sizing: border-box !important;
      }
      
      .chat-container .clarification-hint,
      .chat-container .force-analysis-option,
      .chat-container .message-actions,
      .chat-container .file-attachment {
        max-width: 90% !important;
        width: 90% !important;
        box-sizing: border-box !important;
      }
      
      #root .clarification-hint,
      #root .force-analysis-option,
      #root .message-actions,
      #root .file-attachment {
        max-width: 90% !important;
        width: 90% !important;
        box-sizing: border-box !important;
      }
      
      body .clarification-hint,
      body .force-analysis-option,
      body .message-actions,
      body .file-attachment {
        max-width: 90% !important;
        width: 90% !important;
        box-sizing: border-box !important;
      }
    }
  `;
  
  // 避免重复添加
  if (!document.querySelector('#mobile-overflow-fix')) {
    style.id = 'mobile-overflow-fix';
    document.head.appendChild(style);
  }
};

/**
 * 初始化视口高度修复
 */
export const initViewportHeightFix = (): (() => void) => {
  // 初始设置
  setViewportHeight();
  fixMobileLayoutOffset();
  preventMobileZoom();
  fixLongTextOverflow();
  
  // 监听视口变化
  const handleResize = () => {
    setViewportHeight();
    fixMobileLayoutOffset();
  };
  
  const handleVisualViewportChange = () => {
    setViewportHeight();
    fixMobileLayoutOffset();
  };
  
  // 添加事件监听器
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);
  
  // 监听视觉视口变化（移动端键盘弹出等）
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleVisualViewportChange);
  }
  
  // 监听DOM变化，确保新添加的内容也应用样式
  const observer = new MutationObserver(() => {
    if (isMobileDevice()) {
      fixLongTextOverflow();
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // 返回清理函数
  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
    }
    observer.disconnect();
  };
};

/**
 * 检查是否为移动设备
 */
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * 获取安全的底部间距（考虑安全区域）
 */
export const getSafeBottomPadding = (): string => {
  if (isMobileDevice()) {
    return 'env(safe-area-inset-bottom, 0px)';
  }
  return '0px';
};

/**
 * 获取输入区域高度（用于计算消息区域的底部间距）
 */
export const getInputAreaHeight = (): number => {
  // 基础高度 + 安全区域
  const baseHeight = 80; // 输入区域基础高度
  const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom') || '0');
  
  return baseHeight + safeAreaBottom;
}; 